// @flow
import api from '../api/itemFacets';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_ITEM_FACETS_FAIL = 'la/domain/itemFacet/LOAD_FAIL';
export const LOAD_ITEM_FACETS_REQUEST = 'la/domain/itemFacet/LOAD_REQUEST';
export const LOAD_ITEM_FACETS_SUCCESS = 'la/domain/itemFacet/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_ITEM_FACETS_FAIL:
            return {
                ...state,
                loading: difference(cloneDeep(state.loading), action.meta.itemIds),
            };
        case LOAD_ITEM_FACETS_REQUEST:
            return {
                ...state,
                loading: union(cloneDeep(state.loading), action.payload),
            };
        case LOAD_ITEM_FACETS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            cloneDeep(action.payload).forEach(facets => {
                const { itemId } = facets;
                delete facets.itemId;
                existing[itemId] = { ...facets };
                loaded[itemId] = time;
                loading = difference(loading, [itemId]);
            });

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.itemFacets;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getItemFacets = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

export const getLoadTimeForItemFacets = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

export const isItemFacetsLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchItemFacets = (state, itemId) => {
    const data = getItemFacets(state, itemId);
    if (!isEmpty(data)) {
        const loaded = getLoadTimeForItemFacets(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isItemFacetsLoading(state, itemId);
    return !loading;
};

const whichItemFacetsNeeded = (state, itemIds) => itemIds.filter(itemId => shouldFetchItemFacets(state, itemId));

/* ACTION CREATORS */
const loadItemFacetsFail = (itemIds, error) => ({
    error: true,
    meta: { itemIds },
    payload: error,
    type: LOAD_ITEM_FACETS_FAIL,
});

const loadItemFacetsRequest = itemIds => ({
    payload: itemIds,
    type: LOAD_ITEM_FACETS_REQUEST,
});

const loadItemFacetsSuccess = (itemIds, results) => ({
    meta: { actionTime: Date.now(), itemIds },
    payload: results,
    type: LOAD_ITEM_FACETS_SUCCESS,
});

const loadItemFacets = itemIds => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch(loadItemFacetsRequest(itemIds));
        const response = await api.fetchItemFacets({ deployment, ids: itemIds });
        return dispatch(loadItemFacetsSuccess(itemIds, response.data));
    } catch (error) {
        dispatch(loadItemFacetsFail(itemIds, error));
    }
};

export const fetchItemFacetsIfNeeded = (itemIds: number[]) => async (dispatch: Function, getState: Function) => {
    const neededItems = whichItemFacetsNeeded(getState(), itemIds);
    if (neededItems.length) {
        return dispatch(loadItemFacets(neededItems));
    }
    return Promise.resolve();
};
